// src/pages/StockistsAdminPage.jsx
import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Input,
  Form,
  message,
  Modal,
  Spin,
  Tooltip,
  Row,
  Col,
} from 'antd';
import {
  DeleteOutlined,
  PlusOutlined,
  SearchOutlined,
  ExclamationCircleOutlined,
  EditOutlined,
} from '@ant-design/icons';
import axiosInstance from '../utils/axiosInstance';

const { confirm } = Modal;

export default function StockistsAdminPage() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [addModalVisible, setAddModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [editingRecord, setEditingRecord] = useState(null); // null = creating, object = editing

  const fetchList = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get('/stockists/admin');
      if (res.data && res.data.success) {
        const formatted = (res.data.stockists || []).map((s) => ({
          key: s.id,
          id: s.id,
          name: s.name,
          address: s.address,
          city: s.city || '',
          eircode: s.eircode || '',
          phone: s.phone,
          website: s.website,
          lat: s.lat,
          lng: s.lng,
        }));
        setList(formatted);
      } else {
        setList([]);
        message.error('Failed to load stockists');
      }
    } catch (err) {
      console.error(err);
      message.error('An error occurred while fetching stockists.');
      setList([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchList();
  }, []);

  const openAddModal = () => {
    setEditingRecord(null);
    form.resetFields();
    setAddModalVisible(true);
  };

  const openEditModal = (record) => {
    setEditingRecord(record);
    form.setFieldsValue({
      name: record.name,
      address: record.address,
      city: record.city,
      eircode: record.eircode,
      lat: record.lat,
      lng: record.lng,
      phone: record.phone,
      website: record.website,
    });
    setAddModalVisible(true);
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const lat = parseFloat(values.lat);
      const lng = parseFloat(values.lng);
      if (!isFinite(lat) || !isFinite(lng)) {
        message.error('Please provide valid numeric latitude and longitude.');
        setLoading(false);
        return;
      }

      const payload = {
        name: values.name,
        address: values.address || '',
        city: values.city || '',
        eircode: values.eircode || '',
        phone: values.phone || '',
        website: values.website || '',
        lat,
        lng,
      };

      if (editingRecord) {
        // UPDATE
        const res = await axiosInstance.put(`/stockists/${editingRecord.id}`, payload);
        if (res.data && res.data.success) {
          message.success('Stockist updated');
          form.resetFields();
          setAddModalVisible(false);
          setEditingRecord(null);
          await fetchList();
        } else {
          message.error((res.data && res.data.message) || 'Failed to update stockist');
        }
      } else {
        // CREATE
        const res = await axiosInstance.post('/stockists', payload);
        if (res.status === 201 || (res.data && res.data.success)) {
          message.success('Stockist added');
          form.resetFields();
          setAddModalVisible(false);
          await fetchList();
        } else {
          message.error((res.data && res.data.message) || 'Failed to add stockist');
        }
      }
    } catch (err) {
      console.error(err);
      message.error('Network error while saving stockist');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (id) => {
    confirm({
      title: 'Delete this stockist?',
      icon: <ExclamationCircleOutlined />,
      content: 'This action cannot be undone.',
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        setLoading(true);
        try {
          const res = await axiosInstance.delete(`/stockists/${id}`);
          if (res.data && res.data.success) {
            message.success('Deleted');
            await fetchList();
          } else {
            message.error('Failed to delete');
          }
        } catch (err) {
          console.error(err);
          message.error('Network error while deleting');
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const filtered = list.filter((item) =>
    `${item.name} ${item.address || ''} ${item.city || ''} ${item.eircode || ''}`
      .toLowerCase()
      .includes(searchText.toLowerCase())
  );

  const columns = [
    { title: 'Sr.No', key: 'srno', width: 70, render: (t, r, i) => <div className="text-center">{i + 1}</div> },
    { title: 'Name', dataIndex: 'name', key: 'name', render: (t) => <div className="font-medium">{t}</div> },
    { title: 'Address', dataIndex: 'address', key: 'address', render: (t) => t || '-' },
    { title: 'City', dataIndex: 'city', key: 'city', width: 140, render: (v) => v || '-' },
    { title: 'Eir Code', dataIndex: 'eircode', key: 'eircode', width: 140, render: (v) => v || '-' },
    { title: 'Lat', dataIndex: 'lat', key: 'lat', width: 120, render: (v) => (v !== null && v !== undefined ? v : '-') },
    { title: 'Lng', dataIndex: 'lng', key: 'lng', width: 120, render: (v) => (v !== null && v !== undefined ? v : '-') },
    { title: 'Phone', dataIndex: 'phone', key: 'phone', width: 150 },
    {
      title: 'Actions',
      key: 'actions',
      width: 140,
      align: 'center',
      render: (_, record) => (
        <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
          <Tooltip title="Edit">
            <Button
              icon={<EditOutlined />}
              size="small"
              onClick={() => openEditModal(record)}
            />
          </Tooltip>
          <Tooltip title="Delete">
            <Button danger icon={<DeleteOutlined />} size="small" onClick={() => handleDelete(record.id)} />
          </Tooltip>
        </div>
      ),
    },
  ];

  return (
    <Spin spinning={loading} size="large">
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 py-6 px-4">
        <div className="max-w-7xl mx-auto">
          {/* header & controls */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-6">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
              <div>
                <h1 className="text-2xl font-bold text-gray-900 mb-2">Manage Stockists</h1>
                <p className="text-gray-600">Add, view and manage store locations shown on the public map</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                  <SearchOutlined className="mr-2" />
                  Search stockists
                </label>
                <Input placeholder="Search by name, address, city or eircode..." value={searchText} onChange={(e) => setSearchText(e.target.value)} allowClear className="h-10 rounded-lg" />
              </div>

              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-700 mb-2">
                  <div className="w-3 h-3 bg-blue-500 rounded-full inline-block mr-2" />
                  Stockist Summary
                </label>
                <div className="bg-gray-50 rounded-lg border border-gray-200 p-3 h-10 flex items-center">
                  <div className="text-sm font-medium text-gray-900">Total: {filtered.length} stockists</div>
                </div>
              </div>

              <div className="flex flex-col justify-end">
                <Button type="primary" icon={<PlusOutlined />} onClick={openAddModal} className="h-10 bg-blue-600 hover:bg-blue-700 border-blue-600">Add Stockist</Button>
              </div>

              <div />
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
            <Table dataSource={filtered} columns={columns} rowKey="id" pagination={{ pageSize: 10, showSizeChanger: true, pageSizeOptions: ['10', '20', '50'] }} scroll={{ x: 'max-content' }} className="modern-orders-table" />
          </div>
        </div>
      </div>

      {/* Add / Edit modal (same modal used for both) */}
      <Modal
        title={
          <div className="flex items-center space-x-2">
            {editingRecord ? <EditOutlined className="text-blue-500" /> : <PlusOutlined className="text-blue-500" />}
            <span>{editingRecord ? 'Edit Stockist' : 'Add New Stockist'}</span>
          </div>
        }
        open={addModalVisible}
        onCancel={() => { setAddModalVisible(false); form.resetFields(); setEditingRecord(null); }}
        footer={null}
        className="modern-modal"
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Row gutter={12}>
            <Col span={24}>
              <Form.Item label="Name" name="name" rules={[{ required: true, message: 'Please enter the stockist name' }]}>
                <Input placeholder="Store name" size="large" className="rounded-lg h-12" />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item label="Address" name="address">
                <Input placeholder="Address (optional)" size="large" className="rounded-lg h-12" />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12}>
              <Form.Item label="City" name="city">
                <Input placeholder="City (optional)" size="large" className="rounded-lg h-12" />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12}>
              <Form.Item label="Eir Code" name="eircode">
                <Input placeholder="Eir Code (optional)" size="large" className="rounded-lg h-12" />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12}>
              <Form.Item label="Latitude" name="lat" rules={[{ required: true, message: "Latitude is required (e.g. 53.3493795)" }]}>
                <Input placeholder="Latitude e.g. 53.3493795" size="large" className="rounded-lg h-12" />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12}>
              <Form.Item label="Longitude" name="lng" rules={[{ required: true, message: "Longitude is required (e.g. -6.2605593)" }]}>
                <Input placeholder="Longitude e.g. -6.2605593" size="large" className="rounded-lg h-12" />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item label="Phone" name="phone">
                <Input placeholder="Phone (optional)" size="large" className="rounded-lg h-12" />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item label="Website" name="website">
                <Input placeholder="Website (optional)" size="large" className="rounded-lg h-12" />
              </Form.Item>
            </Col>

            <Col span={24}>
              <div className="flex justify-end space-x-3 mt-2">
                <Button onClick={() => { setAddModalVisible(false); form.resetFields(); setEditingRecord(null); }}>Cancel</Button>
                <Button type="primary" htmlType="submit" className="bg-blue-600 hover:bg-blue-700 border-blue-600">
                  {editingRecord ? 'Save Changes' : 'Add Stockist'}
                </Button>
              </div>
            </Col>
          </Row>
        </Form>
      </Modal>

      <style jsx>{`
        .modern-orders-table .ant-table-thead > tr > th {
          background: #f8fafc;
          color: #374151;
          font-weight: 600;
          border-bottom: 2px solid #e5e7eb;
          white-space: nowrap;
          padding: 12px 16px;
        }
        .modern-orders-table .ant-table-tbody > tr > td {
          border-bottom: 1px solid #f1f5f9;
          padding: 12px 16px;
          transition: all 0.2s ease;
        }
        .modern-modal .ant-modal-content {
          border-radius: 16px;
          overflow: hidden;
        }
        .modern-modal .ant-modal-header {
          border-bottom: 1px solid #e5e7eb;
          background: #f8fafc;
        }
        .ant-input {
          border-radius: 8px !important;
        }
      `}</style>
    </Spin>
  );
}
